/**
 * BreakGlassBanner — unmissable red banner shown on every authenticated
 * page while a support session is active. Pinned to the very top of
 * the layout, above the Trial / Impersonation banners.
 *
 * Behaviour:
 *   - Polls `/api/platform/break-glass/active` every 20s so the countdown
 *     stays accurate and the banner disappears within one tick after a
 *     background sweep expires the session.
 *   - Decrements the local countdown every second between polls.
 *   - Zero visibility to tenant users — the banner only renders for
 *     platform admins, matching the endpoint's auth gate.
 *   - "End session" button terminates immediately and clears the stored
 *     header so the next request goes back to the admin's home scope.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { breakGlassAPI, setBreakGlassSessionId, getBreakGlassSessionId } from '../lib/api';
import { Button } from './ui/button';
import { AlertOctagon, X, Clock } from 'lucide-react';

const POLL_MS = 20_000;

function formatRemaining(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'expiring…';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

export default function BreakGlassBanner() {
  const { user, loading: authLoading } = useAuth();
  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [ending, setEnding] = useState(false);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const fetchActive = useCallback(async () => {
    if (!user?.is_platform_admin) {
      setSession(null);
      return;
    }
    try {
      const res = await breakGlassAPI.active();
      const s = res.data;
      if (s && s.id) {
        setSession(s);
        setRemaining(s.seconds_remaining || 0);
        // Keep the stored header id in sync with reality — e.g. in case
        // the admin reloaded the page in a fresh tab.
        if (getBreakGlassSessionId() !== s.id) setBreakGlassSessionId(s.id);
      } else {
        setSession(null);
        setBreakGlassSessionId(null);
      }
    } catch {
      // Treat any error as "no active session" so a stale banner never leaks.
      setSession(null);
    }
  }, [user]);

  // Poll on mount + when user changes
  useEffect(() => {
    if (authLoading || !user?.is_platform_admin) return undefined;
    fetchActive();
    pollRef.current = setInterval(fetchActive, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [authLoading, user, fetchActive]);

  // Second-by-second countdown between polls
  useEffect(() => {
    if (!session) {
      if (tickRef.current) clearInterval(tickRef.current);
      return undefined;
    }
    tickRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          // Hit zero: re-poll immediately so the server's authoritative
          // "expired" state kicks in and the banner disappears.
          fetchActive();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [session, fetchActive]);

  const handleEnd = async () => {
    if (!session) return;
    setEnding(true);
    try {
      await breakGlassAPI.end();
    } catch {
      /* non-fatal — the next poll will reconcile */
    }
    setBreakGlassSessionId(null);
    setSession(null);
    setEnding(false);
  };

  if (!session || !user?.is_platform_admin) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="w-full bg-red-600 text-white border-b border-red-800 shadow-md"
      data-testid="break-glass-banner"
      data-session-id={session.id}
    >
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-2 flex flex-wrap items-center gap-3">
        <AlertOctagon className="w-5 h-5 shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-bold uppercase tracking-wide mr-2" data-testid="break-glass-banner-label">
            Viewing as Platform Administrator
          </span>
          <span className="opacity-90" data-testid="break-glass-banner-target">
            Tenant: <strong>{session.target_organisation_name}</strong>
          </span>
          <span className="opacity-80 hidden md:inline">
            {' '}· {session.reason}
          </span>
        </div>
        <div
          className="flex items-center gap-1 text-sm font-mono bg-red-700/60 px-2 py-0.5 rounded"
          data-testid="break-glass-banner-countdown"
        >
          <Clock className="w-3.5 h-3.5" />
          {formatRemaining(remaining)}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="border-white/40 text-white hover:bg-white/10 h-8"
          onClick={handleEnd}
          disabled={ending}
          data-testid="break-glass-banner-end-btn"
        >
          <X className="w-3.5 h-3.5 mr-1" />
          {ending ? 'Ending…' : 'End session'}
        </Button>
      </div>
    </div>
  );
}
